import { FmsBlockCandidate, TapCandidate } from '../../../models/field-link';

/**
 * Pure filter and grouping rules for the Edit links modal, kept out of the component so each
 * decision can be tested without a template.
 */

export type FmsStatusFilter = 'unlinked' | 'elsewhere' | 'all';
export type TapStatusFilter = 'unassigned' | 'other' | 'all';

/** Taps without a farm prefix are grouped under this. */
export const OTHER_FARM = 'Other';

export interface MoveRow {
  tap: string;
  fromFieldId: number;
  fromFieldName: string | null;
  /** Taps the source field still has once these are moved here. */
  remaining: number;
}

export function blockKey(b: { blockNo: string; fmsFarmId: number }): string {
  return `${b.blockNo}|${b.fmsFarmId}`;
}

/**
 * Fields using the block other than this one. The block this field is already on must not read as
 * "linked elsewhere" just because this field uses it.
 */
export function otherFieldCount(b: FmsBlockCandidate, currentKey: string): number {
  return Math.max(0, b.linkedFieldCount - (blockKey(b) === currentKey ? 1 : 0));
}

export function fmsFarmName(b: FmsBlockCandidate): string {
  return b.farmName ?? `Farm ${b.fmsFarmId}`;
}

export function filterFmsBlocks(
  blocks: FmsBlockCandidate[],
  currentKey: string,
  opts: { search: string; status: FmsStatusFilter; farm: string | null }
): FmsBlockCandidate[] {
  const s = opts.search.trim().toLowerCase();
  return blocks.filter(b => {
    const others = otherFieldCount(b, currentKey);
    if (opts.status === 'unlinked' && others > 0) return false;
    if (opts.status === 'elsewhere' && others === 0) return false;
    if (opts.farm && fmsFarmName(b) !== opts.farm) return false;
    return !s || b.blockNo.toLowerCase().includes(s) || fmsFarmName(b).toLowerCase().includes(s);
  });
}

/**
 * The farm code is the part of the tap name before the first dash (LPH-1303A is LPH). That comes from
 * the naming convention, not from anything iGator returns, so a name without a dash goes to Other.
 */
export function tapFarm(tap: string): string {
  const i = tap.indexOf('-');
  return i > 0 ? tap.slice(0, i).toUpperCase() : OTHER_FARM;
}

export function isOnOtherField(c: TapCandidate, fieldId: string | number): boolean {
  return c.fieldId !== null && String(c.fieldId) !== String(fieldId);
}

/**
 * Taps the user could add. Selected taps never appear, since they sit in the "on this field" list.
 * A tap removed from this field counts as unassigned: saving leaves it on no field.
 */
export function filterTaps(
  taps: TapCandidate[],
  fieldId: string | number,
  selected: Set<string>,
  opts: { search: string; status: TapStatusFilter; farm: string | null }
): TapCandidate[] {
  const s = opts.search.trim().toLowerCase();
  return taps.filter(c => {
    if (selected.has(c.tap)) return false;
    const elsewhere = isOnOtherField(c, fieldId);
    if (opts.status === 'unassigned' && elsewhere) return false;
    if (opts.status === 'other' && !elsewhere) return false;
    if (opts.farm && tapFarm(c.tap) !== opts.farm) return false;
    return !s || c.tap.toLowerCase().includes(s) || (c.fieldName ?? '').toLowerCase().includes(s);
  });
}

/** Farm groups in name order with Other last, taps in name order within each group. */
export function groupTapsByFarm(taps: TapCandidate[]): { farm: string; taps: TapCandidate[] }[] {
  const groups = new Map<string, TapCandidate[]>();
  for (const c of taps) {
    const farm = tapFarm(c.tap);
    groups.set(farm, [...(groups.get(farm) ?? []), c]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (a === OTHER_FARM ? 1 : b === OTHER_FARM ? -1 : a.localeCompare(b)))
    .map(([farm, list]) => ({ farm, taps: [...list].sort((x, y) => x.tap.localeCompare(y.tap)) }));
}

/**
 * One row per selected tap being taken from another field, with how many taps that field keeps.
 * Counted from the candidate list loaded when the modal opened, so only as current as that load.
 */
export function buildMoveRows(taps: TapCandidate[], fieldId: string | number, selected: Set<string>): MoveRow[] {
  const moving = taps.filter(c => selected.has(c.tap) && isOnOtherField(c, fieldId));
  const movingFrom = new Map<number, number>();
  moving.forEach(c => movingFrom.set(c.fieldId!, (movingFrom.get(c.fieldId!) ?? 0) + 1));
  return moving.map(c => ({
    tap: c.tap,
    fromFieldId: c.fieldId!,
    fromFieldName: c.fieldName,
    remaining: taps.filter(t => t.fieldId === c.fieldId).length - movingFrom.get(c.fieldId!)!
  }));
}
